import { isDefined } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';
import { ChakraProps } from '@aloft/toolkit-sui/chakra';
import { Column as ReactTableColumn } from '@tanstack/react-table';

import { useMemo, useRef } from 'react';

import { PinnedColumnEdge, UNSET_COLUMN_SIZE } from './constants';

type Props = {
  paddingX: number;
  numColumns: number;
  edge?: PinnedColumnEdge;
};

export function usePinnedColumnStyles({ paddingX, numColumns, edge = 'border' }: Props) {
  const columnStyleInfo = useRef<{ width: number; left: number }[]>([]);

  const api = useMemo(
    () => ({
      setStyleInfo(column: ReactTableColumn<any, any>) {
        if (!column.getIsPinned()) return;

        const index = column.getPinnedIndex();
        const prev = columnStyleInfo.current[index - 1];
        const width = column.getSize() + paddingX * 2;

        if (column.getSize() === UNSET_COLUMN_SIZE) {
          console.error(
            [
              `Expected explicit size for pinned column at index ${index} but got the react-table default of ${UNSET_COLUMN_SIZE}.`,
              'Ensure the column definition has defined the `size` property.',
            ].join(' '),
          );
        }

        columnStyleInfo.current[index] = {
          width,
          left: prev ? prev.left + prev.width : 0,
        };
      },
      getStyles(column: ReactTableColumn<any, any>): ChakraProps {
        if (!column.getIsPinned()) return {};

        const index = column.getPinnedIndex();
        const { width, left } = columnStyleInfo.current[index] ?? {};
        const isLastPinned = index === numColumns - 1;
        const widthPx = isDefined(width) ? `${width}px` : undefined;

        return {
          boxSizing: 'border-box',
          width: widthPx,
          minWidth: widthPx,
          maxWidth: widthPx,
          position: 'sticky',
          left: `${left ?? 0}px`,
          zIndex: 1,
          ...(isLastPinned && edge === 'scrollShadow' ? { overflow: 'visible' } : {}),
          ...(edge === 'border'
            ? {
                css: {
                  [`:nth-of-type(${numColumns})`]: {
                    borderRight: `2px solid`,
                    borderRightColor: colors.strokeDefault,
                  },
                },
              }
            : {}),
        };
      },
    }),
    [edge, numColumns, paddingX],
  );

  return api;
}
